import { useState } from "react"
import {
  File,
  ThumbsUp,
  ThumbsDown,
  Trash2,
  Download,
  AlertTriangle,
} from "lucide-react"
import { format, parseISO } from "date-fns"
import { Link } from "react-router-dom"
import ReactPlayer from "react-player"
import {
  AiExampleEnum,
  ManualScore,
  QuestionResponse,
  QuestionResponse_CandidateTestResponsesFragment,
  QuestionTypeEnum,
} from "~/__generated__/graphql"
import { gql, useQuery } from "@apollo/client"
import { secondsToMMSS } from "~/common/secondsToMMSS"
import { formatTimeWithSeconds, dateTimePlusSeconds } from "~/common/dates"
import { Button, LinkButton } from "~/shadcn/ui/button"
import { formatPercentage } from "~/common/numbers"
import { QUESTION_TYPE_NAMES } from "~/questions/QuestionGroupForm/utils"
import { ManualScoreForm, scoreLabel } from "~/questions/ManualScoreForm"
import { ChoiceQuestionResponse } from "~/questions/ChoiceQuestionResponse"
import { Score } from "~/ui/Score"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "~/shadcn/ui/collapsible"
import { useCurrentUserMaybe } from "~/auth/CurrentUserContext"
import { useSafeMutation } from "~/common/useSafeMutation"
import { ConfirmDialog } from "~/ui/Confirm"
import toast from "react-hot-toast"
import DOCFileIcon from "~/images/icons/doc_file.svg?react"
import DOCXFileIcon from "~/images/icons/docx_file.svg?react"
import PPTFileIcon from "~/images/icons/ppt_file.svg?react"
import PPTXFileIcon from "~/images/icons/pptx_file.svg?react"
import XLSFileIcon from "~/images/icons/xls_file.svg?react"
import XLSXFileIcon from "~/images/icons/xlsx_file.svg?react"
import PDFFileIcon from "~/images/icons/pdf_file.svg?react"

const ChoiceAnswer = ({
  response,
  name,
}: {
  response: QuestionResponse_CandidateTestResponsesFragment
  name: string
}) => (
  <>
    <div className="text-sm text-gray-999">{name}</div>
    <ChoiceQuestionResponse questionResponse={response} />
  </>
)

const VideoResponseAnswer = ({
  response,
  linkTo,
}: {
  response: QuestionResponse_CandidateTestResponsesFragment
  linkTo: string
}) => {
  const VIDEO_ASPECT_RATIO = 1280 / 720
  const VIDEO_WIDTH = 320
  const VIDEO_HEIGHT = (1 / VIDEO_ASPECT_RATIO) * VIDEO_WIDTH

  const attempt =
    response.questionAttempts[response.questionAttempts.length - 1]

  let transcript = attempt?.transcript

  if (transcript) {
    transcript =
      transcript.slice(0, 300) + (transcript.length > 300 ? "..." : "")
  }

  return (
    <>
      <div className="text-sm text-gray-999">Audio/Video Response</div>
      <div className="flex gap-6">
        <div>
          {attempt?.playbackUrl ? (
            <ReactPlayer
              url={attempt.playbackUrl}
              controls
              width={VIDEO_WIDTH}
              height={VIDEO_HEIGHT}
            />
          ) : attempt ? (
            `Transcoding state: ${attempt.transcodingState}`
          ) : null}
        </div>
        {transcript && (
          <div className="flex flex-col gap-4">
            <div className="text-sm text-gray-999">Transcription</div>
            <div className="text-gray-333 text-lg">
              {transcript}
              <br />
              <Link to={linkTo} className="text-primary">
                Read More
              </Link>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

const TextResponseAnswer = ({
  response,
}: {
  response: QuestionResponse_CandidateTestResponsesFragment
}) => {
  return (
    <>
      <div className="text-sm text-gray-999">Answer</div>
      {response.possiblePaste && (
        <div className="flex items-center px-3 py-2 rounded text-sm border border-yellow-dark bg-yellow-light text-gray-2d2d2d">
          <AlertTriangle size={16} className="inline mr-3 text-yellow-dark" />
          <span className="text-sm">
            This text was potentially pasted from another source.
          </span>
        </div>
      )}
      <div className="text-gray-333 text-lg">
        {response.textResponse?.slice(0, 500)}
        {response.textResponse && response.textResponse.length > 500
          ? "..."
          : ""}
      </div>
    </>
  )
}

const DocumentResponseAnswer = ({
  response,
}: {
  response: QuestionResponse_CandidateTestResponsesFragment
}) => {
  const fileName = response.documentUrl?.split("/").pop()
  const fileExt = fileName?.split(".").pop()?.toLowerCase()

  const getFileIcon = (extension: string) => {
    switch (extension) {
      case "pdf":
        return <PDFFileIcon width={45} height={45} />
      case "doc":
        return <DOCFileIcon width={45} height={45} />
      case "docx":
        return <DOCXFileIcon width={45} height={45} />
      case "xls":
        return <XLSFileIcon width={45} height={45} />
      case "xlsx":
        return <XLSXFileIcon width={45} height={45} />
      case "ppt":
        return <PPTFileIcon width={45} height={45} />
      case "pptx":
        return <PPTXFileIcon width={45} height={45} />
      default:
        return <File width={45} height={45} />
    }
  }

  const getFileTypeName = (extension: string) => {
    switch (extension) {
      case "pdf":
        return "PDF document"
      case "doc":
      case "docx":
        return "Word document"
      case "xls":
      case "xlsx":
        return "Excel spreadsheet"
      case "ppt":
      case "pptx":
        return "PowerPoint presentation"
      default:
        return "Document"
    }
  }

  const cleanFileName = (name: string) => {
    try {
      const decodedName = decodeURIComponent(name)
      const nameWithoutExtension = decodedName.split(".").slice(0, -1).join(".")
      return nameWithoutExtension.replace(/[^a-zA-Z0-9]/g, "_")
    } catch (e) {
      return name
        .replace(/%20/g, "_")
        .replace(/[^a-zA-Z0-9.]/g, "_")
        .split(".")
        .slice(0, -1)
        .join(".")
    }
  }

  const handleDownload = () => {
    if (!response.documentUrl) return

    const link = document.createElement("a")
    link.href = response.documentUrl

    const extension = fileName?.split(".").pop() || ""
    const cleanedName = fileName
      ? `${cleanFileName(fileName)}.${extension}`
      : "document"

    link.download = cleanedName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <>
      <div className="text-sm text-gray-999">Answer</div>
      <div className="border border-gray-E6E6E3 rounded p-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {fileExt ? getFileIcon(fileExt) : "Unknown"}
          </div>
          <div className="flex flex-col w-full">
            <div className="flex justify-between items-center">
              <div className="flex flex-col gap-0.5">
                <span className="font-medium">
                  {fileName ? cleanFileName(fileName) : "Unknown"}
                </span>
                <span className="text-xs text-gray-500">
                  {fileExt ? getFileTypeName(fileExt) : "Unknown"}
                  {" - "}
                  {response.documentSize}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2.5 mr-2.5">
            <Button size="sm" variant="outline-gray" onClick={handleDownload}>
              <Download size={16} />
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}

interface TestQuestionResponseProps {
  response: QuestionResponse_CandidateTestResponsesFragment
  viewPath: (test: QuestionResponse_CandidateTestResponsesFragment) => string
  canEdit?: boolean
}

export const TestQuestionResponse = ({
  response,
  viewPath,
  canEdit,
}: TestQuestionResponseProps) => {
  const { currentUser } = useCurrentUserMaybe()
  const [isScoreResponseFormOpen, setIsScoreResponseFormOpen] = useState(false)
  const [manualScores, setManualScores] = useState<ManualScore[]>(
    (response.manualScores as ManualScore[]) ?? []
  )
  const [averageManualScore, setAverageManualScore] = useState<number | null>(
    response.averageManualScore ?? null
  )
  const { refetch } = useQuery(MANUAL_SCORES_QUERY_DOCUMENT, {
    variables: {
      questionResponseId: response.id,
    },
    onCompleted: (data) => {
      let avgScore
      try {
        avgScore =
          data.manualScores?.nodes.reduce(
            (acc: number, score: ManualScore) => acc + (score.score ?? 0),
            0
          ) / data.manualScores?.nodes.length
      } catch (e) {
        avgScore = null
      }
      setManualScores(data.manualScores?.nodes)
      setAverageManualScore(avgScore)
      return data
    },
  })
  const [destroyManualScore] = useSafeMutation(MANUAL_SCORE_DESTROY_MUTATION)

  const startedAt = (
    response: QuestionResponse_CandidateTestResponsesFragment
  ) => {
    return (
      response.questionAttempts.length > 0 &&
      response.questionAttempts[0].startedAt
    )
  }

  const endedAt = (
    response: QuestionResponse_CandidateTestResponsesFragment
  ) => {
    const startTime = startedAt(response)
    if (response.timeTakenSeconds && startTime) {
      return dateTimePlusSeconds(startTime, response.timeTakenSeconds)
    } else {
      return null
    }
  }

  const deleteScore = async (manualScore: ManualScore) => {
    const res = await destroyManualScore({
      variables: {
        input: {
          manualScoreId: manualScore.id,
        },
      },
    })
    if (res.data?.manualScoreDestroy?.questionResponse) {
      setAverageManualScore(
        res.data?.manualScoreDestroy?.questionResponse?.averageManualScore
      )
    }
    toast.success("Score deleted successfully.")
    refetch()
  }

  return (
    <Collapsible
      open={isScoreResponseFormOpen}
      onOpenChange={setIsScoreResponseFormOpen}
      className="border border-gray-E6E6E3 rounded overflow-hidden"
      key={response.id}
    >
      <div className="grid grid-cols-5">
        <div className="p-6 col-span-4 bg-white flex flex-col gap-4">
          <div className="text-sm text-gray-999">
            {QUESTION_TYPE_NAMES[response.questionGroup.questionType]}
          </div>
          <div className="text-xl text-gray-333">
            {response.question.questionCopy}
          </div>

          {response.questionGroup.questionType ===
            QuestionTypeEnum.VideoResponse && (
            <VideoResponseAnswer
              response={response}
              linkTo={viewPath(response)}
            />
          )}

          {response.questionGroup.questionType ===
            QuestionTypeEnum.TextResponse && (
            <TextResponseAnswer response={response} />
          )}

          {response.questionGroup.questionType ===
            QuestionTypeEnum.SingleChoice && (
            <ChoiceAnswer response={response} name="Single Choice Answer" />
          )}

          {response.questionGroup.questionType ===
            QuestionTypeEnum.MultipleChoice && (
            <ChoiceAnswer response={response} name="Multiple  Choice Answer" />
          )}

          {response.questionGroup.questionType ===
            QuestionTypeEnum.DocumentUpload && (
            <DocumentResponseAnswer response={response} />
          )}
        </div>
        <div className="divide-y divide-gray-E6E6E3 border-l border-gray-E6E6E3">
          <div className="py-3 px-4 flex justify-between items-center">
            <div className="text-gray-333 text-xs">Response Time</div>
            <div className="text-gray-333 text-right">
              {secondsToMMSS(response.timeTakenSeconds) || "-"}
            </div>
          </div>
          <div className="py-3 px-4 flex justify-between items-center">
            <div className="text-gray-333 text-xs">Manual Score</div>
            <div className="text-gray-333 text-right">
              {scoreLabel(averageManualScore) || "-"}
            </div>
          </div>
          <div className="py-3 px-4 flex justify-between items-center">
            <div className="text-gray-333 text-xs">AI Score</div>
            <div className="text-gray-333 text-right min-w-16">
              {response.aiScore !== null && response.aiScore !== undefined ? (
                <Score value={response.aiScore} />
              ) : (
                <span className="text-gray-999">Not scored</span>
              )}
            </div>
          </div>
          <div className="py-3 px-4 flex flex-col justify-between gap-2">
            <div className="flex justify-between gap-1 items-center">
              <div className="text-gray-999 text-xs">Question Difficulty</div>
              <div className="text-gray-333 text-xs text-right">
                {response.questionGroup.difficulty || "-"}
              </div>
            </div>
            <div className="flex justify-between gap-1 items-center">
              <div className="text-gray-999 text-xs">Question Frequency</div>
              <div className="text-gray-333 text-xs text-right">
                {formatPercentage(response.questionGroup.frequencyPercentage) ||
                  "-"}
              </div>
            </div>
            <div className="flex justify-between gap-1 items-center">
              <div className="text-gray-999 text-xs">Start Timestamp</div>
              <div className="text-gray-333 text-xs text-right">
                {!!startedAt(response)
                  ? formatTimeWithSeconds(startedAt(response))
                  : "-"}
              </div>
            </div>
            <div className="flex justify-between gap-1 items-center">
              <div className="text-gray-999 text-xs">End Timestamp</div>
              <div className="text-gray-333 text-xs text-right">
                {!!endedAt(response)
                  ? formatTimeWithSeconds(endedAt(response))
                  : "-"}
              </div>
            </div>
            <div className="flex justify-between gap-1 items-center">
              <div className="text-gray-999 text-xs">Expected Time</div>
              <div className="text-gray-333 text-xs text-right">
                {response.questionGroup.expectedTimeSeconds
                  ? secondsToMMSS(response.questionGroup.expectedTimeSeconds)
                  : "-"}
              </div>
            </div>
            <div className="flex flex-col gap-3 pt-1">
              <LinkButton to={viewPath(response)} size="sm" className="w-full">
                Full Details
              </LinkButton>
              {canEdit && (
                <CollapsibleTrigger asChild>
                  <Button size="sm" variant="outline" className="w-full">
                    Score Response
                  </Button>
                </CollapsibleTrigger>
              )}
            </div>
          </div>
        </div>
      </div>
      <CollapsibleContent>
        <ManualScoreForm
          questionResponse={response as QuestionResponse}
          questionType={response.questionGroup.questionType}
          onSubmitted={() => {
            refetch()
            setIsScoreResponseFormOpen(false)
          }}
        />
      </CollapsibleContent>
      {manualScores.length > 0 && (
        <div className="flex flex-col gap-6 border-t border-gray-E6E6E3 p-6">
          {manualScores?.map((manualScore: any) => (
            <div
              className="flex justify-between items-start"
              key={manualScore.id}
            >
              <div className="flex flex-col gap-1 w-1/4">
                <span className="text-sm">{manualScore.user.name}</span>

                <span className="text-xs text-gray-999">
                  {format(
                    parseISO(manualScore.createdAt),
                    "MMM d, yyyy - h:mm aa"
                  )}
                </span>

                {manualScore.aiExample === AiExampleEnum.Good && (
                  <div className="text-[#155A23] flex gap-2 items-center">
                    <ThumbsUp size={13} />
                    <span className="text-sm font-semibold">
                      Marked as good response example.
                    </span>
                  </div>
                )}
                {manualScore.aiExample === AiExampleEnum.Bad && (
                  <div className="text-error flex gap-2 items-center">
                    <ThumbsDown size={13} />
                    <span className="text-sm font-semibold">
                      Marked as bad response example.
                    </span>
                  </div>
                )}
              </div>
              <div className="flex flex-col w-3/4">
                <div className="flex items-center justify-between w-full gap-6">
                  <div className="text-gray-333 text-sm font-semibold">
                    Manually scored the candidate{" "}
                    {scoreLabel(manualScore.score)}
                  </div>
                  {currentUser &&
                    (currentUser.admin ||
                      currentUser.id === manualScore.user.id) && (
                      <ConfirmDialog
                        text={
                          <>
                            <span className="font-medium">
                              Are you sure you would like to delete this score?"
                            </span>
                          </>
                        }
                        onSuccess={() => deleteScore(manualScore)}
                      >
                        <Button size="sm" variant="ghost">
                          <Trash2 size={16} />
                        </Button>
                      </ConfirmDialog>
                    )}
                </div>
                <div className="text-gray-333 text-sm">{manualScore.note}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Collapsible>
  )
}

const MANUAL_SCORES_QUERY_DOCUMENT = gql(/* GraphQL */ `
  query manualScores($questionResponseId: ID!) {
    manualScores(questionResponseId: $questionResponseId) {
      nodes {
        id
        score
        note
        aiExample
        createdAt
        user {
          id
          name
        }
      }
    }
  }
`)

const MANUAL_SCORE_DESTROY_MUTATION = gql(`
  mutation ManualScoreDestroy($input: ManualScoreDestroyInput!) {
    manualScoreDestroy(input: $input) {
      manualScore {
        id
      }
      questionResponse {
        id
        averageManualScore
      }
    }
  }
`)
